import React, { useState } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import NDRMetricCards from './components/NDRMetricCards';
import NDRFiltersBar from './components/NDRFiltersBar';
import NDRTable from './components/NDRTable';
import NDRDrawer from './components/NDRDrawer';
import Icon from '../../components/AppIcon';
import api from '../../api/axios';

const getDateRange = (filters) => {
  const now = new Date();
  let from = new Date();
  let to = new Date();
  if (filters.dateRange === 'last7') {
    from.setDate(now.getDate() - 6);
  } else if (filters.dateRange === 'last30') {
    from.setDate(now.getDate() - 29);
  } else if (filters.dateRange === 'lastMonth') {
    from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    to = new Date(now.getFullYear(), now.getMonth(), 0);
  } else if (filters.dateRange === 'custom' && filters.customFrom && filters.customTo) {
    from = new Date(filters.customFrom);
    to = new Date(filters.customTo);
  } else {
    from.setFullYear(now.getFullYear() - 1);
  }
  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  const fmt = (d) => d.toISOString().split('.')[0];
  return { from: fmt(from), to: fmt(to) };
};

const NDRManagement = () => {
  const [filters, setFilters] = useState({
    dateRange: 'last7',
    search: '',
    ndrStatus: 'all',
    callOutcome: 'all',
    customFrom: '',
    customTo: '',
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const range = getDateRange(filters);

  const handleExport = async (format) => {
    setExportOpen(false);
    if (format !== 'csv') return;
    try {
      setDownloading(true);
      const params = { from: range.from, to: range.to };
      if (filters.ndrStatus && filters.ndrStatus !== 'all') params.status = filters.ndrStatus;
      if (filters.callOutcome && filters.callOutcome !== 'all') params.callOutcome = filters.callOutcome;
      if (filters.search) params.search = filters.search;

      const response = await api.get('/api/v1/ndr-management/export', {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ndr-management-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content min-h-screen bg-background flex flex-col">
        <NavigationBreadcrumb />

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between px-4 md:px-6 py-4 bg-white border-b border-gray-100">
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">NDR Management</h1>
            <p className="text-xs text-gray-400 mt-0.5">Non-Delivery Report — attempt-wise tracking & resolution</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Refresh */}
            <button
              onClick={handleRefresh}
              className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              title="Refresh"
            >
              <Icon name="RefreshCw" size={16} />
            </button>

            {/* Export dropdown */}
            <div className="relative">
              <button
                onClick={() => setExportOpen(!exportOpen)}
                disabled={downloading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                <Icon name="Download" size={16} />
                <span className="hidden sm:inline">{downloading ? 'Downloading...' : 'Download Data Dump'}</span>
                <span className="sm:hidden">{downloading ? '...' : 'Export'}</span>
                <Icon name="ChevronDown" size={14} />
              </button>
              {exportOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 w-40 py-1">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 transition-colors"
                  >
                    <Icon name="FileText" size={14} />
                    Export CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Filters Bar */}
        <NDRFiltersBar filters={filters} onChange={setFilters} />

        {/* Main Content */}
        <div className="flex-1 px-4 md:px-6 py-5 overflow-auto">
          {/* Metric Cards */}
          <NDRMetricCards from={range.from} to={range.to} refreshKey={refreshKey} />

          {/* Table */}
          <NDRTable
            from={range.from}
            to={range.to}
            filters={filters}
            refreshKey={refreshKey}
            onRowClick={(order) => setSelectedOrder(order)}
          />
        </div>

        {/* Side Drawer */}
        {selectedOrder && (
          <NDRDrawer
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
          />
        )}
      </div>
    </>
  );
};

export default NDRManagement;
